import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, Check, MailCheck, MailWarning, AlertCircle } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";

const INDUSTRIES = ["Cement manufacturing", "Ready-mix concrete", "Cement & concrete", "Other construction materials"];

function Shell({ children, step }) {
  const steps = ["Your account", "Your organisation", "Choose plan"];
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b bg-white">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/get-started" className="flex items-center gap-2.5">
            <BrandMark size={32} />
            <span className="font-display font-semibold">EPD Nigeria</span>
          </Link>
          <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900">Sign in</Link>
        </div>
      </header>
      <div className="max-w-xl mx-auto px-6 py-10">
        {step >= 0 && (
          <div className="flex items-center gap-2 mb-8">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className={`h-7 w-7 rounded-full text-xs flex items-center justify-center font-medium ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-[#0f172a] text-white" : "bg-slate-200 text-slate-500"}`}>{i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}</div>
                <span className={`text-xs ${i === step ? "text-slate-900 font-medium" : "text-slate-400"} hidden sm:inline`}>{s}</span>
                {i < steps.length - 1 && <div className="h-px flex-1 bg-slate-200" />}
              </div>
            ))}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

const Field = ({ label, k, form, set, type = "text", req }) => (
  <div>
    <Label className="text-xs">{label}{req && " *"}</Label>
    <Input type={type} data-testid={`reg-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1" />
  </div>
);

export function Register() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ country: "Nigeria", plan: sp.get("plan") || "Annual", contracted_named_users: Math.max(1, parseInt(sp.get("users"), 10) || 1) });
  const [terms, setTerms] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next1 = () => {
    setErr("");
    if (!form.first_name || !form.last_name || !form.email || !form.password) return setErr("Please complete all required fields.");
    if (form.password.length < 8) return setErr("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return setErr("Passwords do not match.");
    if (!terms) return setErr("Please accept the Terms of Service and Privacy Notice.");
    setStep(1);
  };
  const next2 = () => { setErr(""); if (!form.legal_name) return setErr("Legal organisation name is required."); setStep(2); };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/register", { ...form, accept_terms: terms });
      setDone(data);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  if (done) return (
    <Shell step={-1}>
      <div className="bg-white border rounded-lg p-8 text-center">
        <div className="h-12 w-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto"><MailCheck className="h-6 w-6 text-emerald-600" /></div>
        <h2 className="font-display text-2xl font-semibold mt-4">Registration received</h2>
        <p className="text-sm text-muted-foreground mt-2">Your EPD Nigeria workspace for <b>{done.organization}</b> has been created.</p>
        <p className="text-sm text-slate-600 mt-4">We've sent a verification link to <b>{form.email}</b>. Verify your email to continue to payment and activate your EPD Nigeria workspace.</p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
          <Button data-testid="reg-check-email">Check your email</Button>
          <Link to="/login"><Button variant="outline" data-testid="reg-to-login">Go to sign in</Button></Link>
        </div>
      </div>
    </Shell>
  );

  return (
    <Shell step={step}>
      {step === 0 && (
        <div className="bg-white border rounded-lg p-6">
          <h2 className="font-display text-2xl font-semibold">Create your EPD Nigeria account</h2>
          <p className="text-sm text-muted-foreground mt-1">Set up your organisation's Environmental Product Declaration workspace.</p>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <Field label="First name" k="first_name" form={form} set={set} req />
            <Field label="Last name" k="last_name" form={form} set={set} req />
            <div className="col-span-2"><Field label="Work email" k="email" form={form} set={set} type="email" req /></div>
            <Field label="Job title" k="job_title" form={form} set={set} />
            <Field label="Phone number" k="phone" form={form} set={set} />
            <Field label="Password" k="password" form={form} set={set} type="password" req />
            <Field label="Confirm password" k="confirm" form={form} set={set} type="password" req />
          </div>
          <label className="flex items-start gap-2 mt-4 text-sm text-slate-600">
            <input type="checkbox" data-testid="reg-terms" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1" />
            <span>I accept the <a className="text-primary underline" href="/terms" target="_blank" rel="noreferrer">Terms of Use</a> and <a className="text-primary underline" href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.</span>
          </label>
          {err && <p className="text-sm text-destructive mt-3" data-testid="reg-error">{err}</p>}
          <Button className="w-full mt-5" data-testid="reg-continue-1" onClick={next1}>Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>
          <p className="text-sm text-center mt-3 text-slate-500">Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link></p>
        </div>
      )}
      {step === 1 && (
        <div className="bg-white border rounded-lg p-6">
          <h2 className="font-display text-2xl font-semibold">Tell us about your organisation</h2>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="col-span-2"><Field label="Legal organisation name" k="legal_name" form={form} set={set} req /></div>
            <Field label="Trading name" k="trading_name" form={form} set={set} />
            <Field label="Company registration number" k="registration_number" form={form} set={set} />
            <Field label="Country" k="country" form={form} set={set} />
            <Field label="Headquarters" k="headquarters" form={form} set={set} />
            <Field label="Website" k="website" form={form} set={set} />
            <div>
              <Label className="text-xs">Industry</Label>
              <select data-testid="reg-industry" value={form.industry || ""} onChange={(e) => set("industry", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                <option value="">Select…</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>
          {err && <p className="text-sm text-destructive mt-3" data-testid="reg-error">{err}</p>}
          <div className="flex gap-3 mt-5">
            <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            <Button className="flex-1" data-testid="reg-continue-2" onClick={next2}>Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="bg-white border rounded-lg p-6">
          <h2 className="font-display text-2xl font-semibold">Choose your plan</h2>
          <div className="grid grid-cols-2 gap-4 mt-6">
            {[["Monthly", "₦2,500,000", "/month", ""], ["Annual", "₦25,000,000", "/year", "Save ₦5,000,000"]].map(([name, price, unit, note]) => (
              <button key={name} data-testid={`reg-plan-${name.toLowerCase()}`} onClick={() => set("plan", name)}
                className={`text-left rounded-lg border p-4 ${form.plan === name ? "border-emerald-400 ring-1 ring-emerald-200 bg-emerald-50/40" : ""}`}>
                <div className="text-sm font-medium">Enterprise {name}</div>
                <div className="font-mono text-lg font-semibold mt-1">{price}<span className="text-xs text-slate-400 font-sans">{unit}</span></div>
                {note && <div className="text-xs text-emerald-700 mt-0.5">{note}</div>}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">After registering, verify your email to continue to secure online payment and activate your workspace.</p>
          {err && <p className="text-sm text-destructive mt-3" data-testid="reg-error">{err}</p>}
          <div className="flex gap-3 mt-5">
            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            <Button className="flex-1" data-testid="reg-submit" onClick={submit} disabled={busy}>{busy ? "Submitting…" : "Submit registration"}</Button>
          </div>
        </div>
      )}
    </Shell>
  );
}

function ActivationStatus({ ctx }) {
  if (ctx.org_active)
    return (
      <>
        <p className="text-sm text-slate-600 mt-2">Your workspace is active. You can sign in and get started.</p>
        <Link to="/login"><Button className="mt-5" data-testid="verify-continue">Continue to workspace</Button></Link>
      </>
    );
  return (
    <>
      <p className="text-sm text-slate-600 mt-2">Your email is verified. Continue to payment to activate your workspace.</p>
      {ctx.organization && (
        <div className="mt-3 text-sm rounded-lg border bg-slate-50 p-3 inline-block">
          <div><b>{ctx.organization}</b></div>
          <div className="text-slate-500">Plan: {ctx.plan}</div>
        </div>
      )}
      <div className="mt-5">
        <Link to="/login"><Button data-testid="verify-proceed-payment">Proceed to payment</Button></Link>
      </div>
      <p className="text-xs text-muted-foreground mt-3">Sign in to complete your payment securely with Paystack.</p>
    </>
  );
}

export function VerifyEmail() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [ctx, setCtx] = useState({});
  const [busy, setBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  useEffect(() => {
    // GET only reads state — it must never consume the token.
    api.get(`/auth/verify-email/${token}`)
      .then(({ data }) => { setState(data.state); setCtx(data); })
      .catch(() => setState("INVALID"))
      .finally(() => setLoading(false));
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email", { token });
      setState(data.state); setCtx((c) => ({ ...c, ...data }));
    } catch { setState("INVALID"); } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true); setResendMsg("");
    try {
      const { data } = await api.post("/auth/resend-verification", { email: ctx.email });
      setResendMsg(data.message);
    } catch (e) { setResendMsg(apiError(e)); } finally { setBusy(false); }
  };

  const Icon = ({ variant }) => {
    const map = {
      ok: { C: Check, cls: "bg-emerald-50 border-emerald-200 text-emerald-600" },
      warn: { C: MailWarning, cls: "bg-amber-50 border-amber-200 text-amber-600" },
      err: { C: AlertCircle, cls: "bg-rose-50 border-rose-200 text-rose-600" },
      mail: { C: MailCheck, cls: "bg-emerald-50 border-emerald-200 text-emerald-600" },
    }[variant];
    return (
      <div className={`h-12 w-12 rounded-full border flex items-center justify-center mx-auto ${map.cls}`}>
        <map.C className="h-6 w-6" />
      </div>
    );
  };

  return (
    <Shell step={-1}>
      <div className="bg-white border rounded-lg p-8 text-center" data-testid={`verify-state-${(state || "loading").toLowerCase()}`}>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : state === "PENDING" ? (
          <>
            <Icon variant="mail" />
            <h2 className="font-display text-2xl font-semibold mt-4">Confirm your email address</h2>
            <p className="text-sm text-slate-600 mt-2">
              Click the button below to verify <b>{ctx.email}</b> and secure your EPD Nigeria account.
            </p>
            <Button className="mt-6" data-testid="verify-confirm-btn" onClick={confirm} disabled={busy}>
              {busy ? "Verifying…" : "Verify email address"}
            </Button>
          </>
        ) : state === "VERIFIED_NOW" ? (
          <>
            <Icon variant="ok" />
            <h2 className="font-display text-2xl font-semibold mt-4" data-testid="verify-success">Email verified</h2>
            <p className="text-sm text-slate-600 mt-2">Your email has been verified successfully.</p>
            <ActivationStatus ctx={ctx} />
          </>
        ) : state === "ALREADY_VERIFIED" ? (
          <>
            <Icon variant="ok" />
            <h2 className="font-display text-2xl font-semibold mt-4" data-testid="verify-already">Already verified</h2>
            <p className="text-sm text-slate-600 mt-2">Your email is already verified.</p>
            <ActivationStatus ctx={ctx} />
          </>
        ) : state === "EXPIRED" ? (
          <>
            <Icon variant="warn" />
            <h2 className="font-display text-xl font-semibold mt-4 text-slate-900" data-testid="verify-expired">Link expired</h2>
            <p className="text-sm text-muted-foreground mt-2">This verification link has expired.</p>
            {resendMsg ? (
              <p className="text-sm text-emerald-700 mt-4" data-testid="verify-resend-msg">{resendMsg}</p>
            ) : (
              <Button className="mt-5" data-testid="verify-resend-btn" onClick={resend} disabled={busy}>
                {busy ? "Sending…" : "Resend verification email"}
              </Button>
            )}
            <div className="mt-4"><Link to="/login" className="text-sm text-primary hover:underline">Go to sign in</Link></div>
          </>
        ) : (
          <>
            <Icon variant="err" />
            <h2 className="font-display text-xl font-semibold mt-4 text-slate-900" data-testid="verify-invalid">Invalid link</h2>
            <p className="text-sm text-muted-foreground mt-2">
              This verification link is invalid or has already been used.
            </p>
            <p className="text-sm text-slate-600 mt-2">
              Need help? Contact <a className="text-primary hover:underline" href="mailto:info@epd-nigeria.com">info@epd-nigeria.com</a>.
            </p>
            <Link to="/login"><Button variant="outline" className="mt-5">Go to sign in</Button></Link>
          </>
        )}
      </div>
    </Shell>
  );
}
